import { Session } from '../AuthContext';
import { PermissionLevel } from '../../system/permission';

interface CreateParams {
  propertyType?: string;
  listAgent?: string;
  allowOnlineApplication?: boolean;
}

export const extractCreateParams = (): CreateParams => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);

  // Check if we have the new 'init' parameter
  const initParam = urlParams.get('init');

  if (initParam) {
    try {
      // Decode the base64 string to JSON
      const jsonString = atob(initParam);

      // Parse the JSON string to get our parameters object
      const params = JSON.parse(jsonString);

      return {
        propertyType: params.propertyType || undefined,
        listAgent: params.listAgent || undefined,
        allowOnlineApplication: params.allowOnlineApplication || undefined,
        // Add any additional parameters as needed
      };
    } catch (error) {
      console.error('Error decoding init parameter:', error);
      // Fall back to direct URL parameters if decoding fails
    }
  }

  // Fallback to direct URL parameters (for backward compatibility)
  const propertyType = urlParams.get('propertyType') || undefined;
  const listAgent = urlParams.get('listAgent') || undefined;
  const allowOnlineApplication =
    urlParams.get('allowOnlineApplication') === 'true' || undefined;

  return {
    propertyType,
    listAgent,
    allowOnlineApplication,
  };
};

export const isPersonalAssistant = (session: Session | null): boolean => {
  return session?.user?.agent?.MemberType === 'Personal Assistant';
};

export const getListAgentPermissionLevel = (
  session: Session | null,
): PermissionLevel | null => {
  if (!session?.permissions) return null;

  // Look for permission: Access Listing Management
  const accessListingManagementPermissions = session.permissions.filter(
    (permission) =>
      permission.PartyPermPermissionType === 'Access Listing Management',
  );

  // Check for Brokerage level first (highest priority)
  const hasBrokerageLevel = accessListingManagementPermissions.some(
    (permission) =>
      permission.PartyPermAccessLevelType === PermissionLevel.BROKERAGE,
  );

  if (hasBrokerageLevel) return PermissionLevel.BROKERAGE;

  // Check for Individual level next
  const hasIndividualLevel = accessListingManagementPermissions.some(
    (permission) =>
      permission.PartyPermAccessLevelType === PermissionLevel.INDIVIDUAL,
  );

  if (hasIndividualLevel) return PermissionLevel.INDIVIDUAL;

  // Check for Office level
  const officePermissions = accessListingManagementPermissions.filter(
    (permission) =>
      permission.PartyPermAccessLevelType === PermissionLevel.OFFICE,
  );

  if (officePermissions.length > 0) return PermissionLevel.OFFICE;

  return null;
};

export const getOfficeKeysFromPermissions = (
  session: Session | null,
): string[] => {
  if (!session?.permissions) return [];

  // Look for Office level permissions
  const officePermissions = session.permissions.filter(
    (permission) =>
      permission.PartyPermPermissionType === 'Access Listing Management' &&
      permission.PartyPermAccessLevelType === PermissionLevel.OFFICE,
  );

  // Extract the office keys from the grantor party keys
  return officePermissions
    .map((permission) => permission.PartyPermGrantorPartyKey)
    .map((grantorKey) => `${grantorKey}`);
};
